<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-row dense no-gutters>
      <v-col cols="12" md="6">
        <v-text-field
          dense
          single-line
          outlined
          :prepend-icon="dense ? null : 'person'"
          ref="nameInput"
          name="name"
          v-model="form.name"
          :label="$t('delivery.order.labels.name')"
          :placeholder="$t('delivery.order.labels.name')"
          :error="hasInputErrors('name')"
          :error-messages="getInputErrors('name')"
          color="secondary"
          :rules="nameRules"
          required
          class="required"
          autofocus
          @keydown="preventInvalidNameKey"
          @blur="handleNameBlur"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          dense
          single-line
          outlined
          :prepend-icon="dense ? null : 'phone'"
          ref="phoneInput"
          name="phone"
          v-model="form.phone"
          :label="$t('delivery.order.labels.phone')"
          :placeholder="$t('delivery.order.labels.phone')"
          :error="hasInputErrors('phone')"
          :error-messages="getInputErrors('phone')"
          color="secondary"
          :rules="phoneRules"
          :required="!isSalon"
          :class="isSalon ? '' : 'required'"
          @keydown="preventInvalidPhoneKey"
          @blur="validatePhone"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          dense
          single-line
          outlined
          :prepend-icon="dense ? null : 'email'"
          ref="emailInput"
          name="email"
          v-model="form.email"
          :label="$t('delivery.order.labels.email')"
          :placeholder="$t('delivery.order.labels.email')"
          :error="hasInputErrors('email')"
          :error-messages="getInputErrors('email')"
          color="secondary"
          :rules="emailRules"
          required
          :class="{ required: emailRequired }"
          @blur="validateEmail"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          dense
          hide-details
          single-line
          outlined
          :rows="dense ? 2 : 2"
          :prepend-icon="dense ? null : 'description'"
          name="observations"
          v-model="form.observations"
          :label="$t('delivery.order.labels.observations')"
          :placeholder="$t('delivery.order.labels.observations')"
          :error="hasInputErrors('observations')"
          :error-messages="getInputErrors('observations')"
          color="secondary"
        ></v-text-field>
      </v-col>

      <v-col v-if="nextButton" cols="12" class="text-right">
        <submit-button
          text="common.next"
          @click="submitContact"
          :disabled="!this.isValid"
        ></submit-button>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { InputErrorsByProps, RequiredRule } from "@dracul/common-frontend";
import { SubmitButton } from "@dracul/common-frontend";

export default {
  name: "BarContactForm",
  mixins: [InputErrorsByProps, RequiredRule],
  components: { SubmitButton },
  props: {
    nextButton: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
    emailRequired: { type: Boolean, default: false },
  },
  data() {
    return {
      submitDisabled: true,
      valid: false,
    };
  },

  computed: {
    form: {
      get() {
        return this.$store.getters.orderSystem.order.contact;
      },
      set(val) {
        this.$store.commit("orderContact", val);
      },
    },
    emailRules() {
      return [
        (v) =>
          !this.emailRequired ||
          !!(v && v.trim()) ||
          this.$t("user.validation.required"),
        (v) =>
          this.isValidEmail(v) ||
          "Formato inválido. Revisá que el email esté bien escrito.",
      ];
    },
    phoneRules() {
      return [
        (v) => !!v || this.$t("user.validation.required"),
        (v) =>
          this.isValidPhone(v) ||
          "Formato inválido. Ingrese 10 dígitos (código de área sin 0 y número sin 15).",
      ];
    },
    nameRules() {
      return [
        (v) => !!(v && v.trim()) || this.$t("user.validation.required"),
        (v) =>
          this.isValidName(v) ||
          "El nombre solo puede contener letras y espacios.",
      ];
    },
    isValid() {
      const name = (this.form.name || "").trim();
      const phone = this.form.phone || "";
      const email = (this.form.email || "").trim();
      const isPhoneReady = this.isSalon
        ? phone === "" || this.isValidPhone(phone)
        : phone !== "" && this.isValidPhone(phone);

      return (
        this.valid &&
        name !== "" &&
        this.isValidName(name) &&
        (!this.emailRequired || email !== "") &&
        this.isValidEmail(email) &&
        isPhoneReady
      );
    },

    isSalon() {
      return this.$store.getters.orderSystem.order.origin === "SALON";
    },
  },
  methods: {
    isValidName(value) {
      return !value || /^[A-Za-zÀ-ÖØ-öø-ÿÑñ ]+$/.test(value);
    },
    isValidNameKey(key) {
      return /^[A-Za-zÀ-ÖØ-öø-ÿÑñ ]$/.test(key);
    },
    preventInvalidNameKey(event) {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
        return;
      }

      if (!this.isValidNameKey(event.key)) {
        event.preventDefault();
      }
    },
    formatName(value) {
      return (value || "")
        .trim()
        .toLocaleLowerCase("es-AR")
        .replace(/(^| )([A-Za-zÀ-ÖØ-öø-ÿÑñ])/g, (match, space, letter) => {
          return space + letter.toLocaleUpperCase("es-AR");
        });
    },
    validateForm() {
      return this.$refs.form.validate();
    },
    validateName() {
      return this.$refs.nameInput.validate();
    },
    validateEmail() {
      return this.$refs.emailInput.validate();
    },
    validatePhone() {
      return this.$refs.phoneInput.validate();
    },
    normalizeName() {
      this.form.name = this.formatName(this.form.name);
    },
    handleNameBlur() {
      if (this.validateName()) {
        this.normalizeName();
      }
    },
    normalizeEmail() {
      this.form.email = (this.form.email || "").trim();
    },
    submitContact() {
      if (this.validateForm()) {
        this.normalizeName();
        this.normalizeEmail();
        this.$emit("next");
      }
    },
    isValidEmail(value) {
      const email = (value || "").trim();
      return (
        email === "" ||
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)
      );
    },
    isValidPhone(value) {
      return /^(?!0)(?!15)\d{10}$/.test(value || "");
    },
    isValidPhoneKey(key) {
      return /^[0-9]$/.test(key);
    },
    preventInvalidPhoneKey(event) {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
        return;
      }

      if (!this.isValidPhoneKey(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped></style>
